/**
 * SupplierLeadsPage — /dashboard/supplier/leads
 *
 * Workflow-focused leads inbox. 3 filter pills (New / Responded / Booked)
 * with live counts, smart-sorted New tab (unread-newest first), whole-row
 * clickable, multi-lead upgrade banner for free suppliers.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Fire, Clock, PaperPlaneRight, CheckCircle, Lightning } from '@phosphor-icons/react';
import PushSoftAsk, { shouldShowSoftAsk } from '../components/PushSoftAsk';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'new', label: 'New' },
  { key: 'responded', label: 'Responded' },
  { key: 'booked', label: 'Booked' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min${s < 120 ? '' : 's'} ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

const SupplierLeadsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [active, setActive] = useState(searchParams.get('tab') || 'new');
  const [isPro, setIsPro] = useState(false);
  const [replyStats, setReplyStats] = useState(null);
  const [showSoftAsk, setShowSoftAsk] = useState(false);

  const fromDigest = searchParams.get('from') === 'digest';

  const refresh = useCallback(async () => {
    try {
      const [lds, q, rs] = await Promise.all([
        axios.get(`${API}/suppliers/me/leads`),
        axios.get(`${API}/suppliers/me/chat-quota`),
        axios.get(`${API}/suppliers/me/reply-stats`),
      ]);
      setLeads(lds.data.leads || []);
      setIsPro(!!q.data.is_pro);
      setReplyStats(rs.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate('/login');
      else if (err?.response?.status === 404) toast.error('Please complete your supplier profile first');
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  // Contextual push permission: fire soft-ask after we confirm the
  // supplier has at least one lead (earned value → highest opt-in rate)
  useEffect(() => {
    if (!leads || leads.length === 0) return;
    if (shouldShowSoftAsk('supplier_lead')) {
      const t = setTimeout(() => setShowSoftAsk(true), 1500);
      return () => clearTimeout(t);
    }
  }, [leads]);

  const counts = useMemo(() => {
    if (!leads) return { new: 0, responded: 0, booked: 0 };
    return leads.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, { new: 0, responded: 0, booked: 0 });
  }, [leads]);

  const visible = useMemo(() => {
    if (!leads) return [];
    const rows = leads.filter((l) => l.status === active);
    // Smart sort for New: unread desc → newest → then older
    if (active === 'new') {
      rows.sort((a, b) => {
        const au = (a.unread_count || 0) > 0 ? 1 : 0;
        const bu = (b.unread_count || 0) > 0 ? 1 : 0;
        if (au !== bu) return bu - au;
        return (b.last_customer_message_at || b.created_at || '').localeCompare(a.last_customer_message_at || a.created_at || '');
      });
    } else if (active === 'responded') {
      rows.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    } else {
      rows.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    }
    return rows;
  }, [leads, active]);

  const showMultiLeadUpgrade = !isPro && counts.new >= 2;

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="supplier-leads-page">
      <div className="max-w-2xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate('/dashboard/supplier')}
          className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </button>

        <h1 className="text-3xl font-black mb-1">Leads</h1>
        {fromDigest && counts.new > 0 && (
          <p className="text-[#C29D52] text-xs font-bold mb-3" data-testid="from-digest-banner">
            {counts.new} new {counts.new === 1 ? 'enquiry' : 'enquiries'} waiting for your response
          </p>
        )}
        {!fromDigest && <p className="text-white/40 text-xs mb-3">Respond quickly to increase your chances of booking.</p>}

        {/* Reply-time pill — only when we have enough data to be directional */}
        {replyStats && replyStats.my_avg_seconds !== null && (
          <ReplyTimePill stats={replyStats} />
        )}

        {/* Micro-feedback: surface WHY they're getting leads when bucket is strong/elite */}
        {replyStats && replyStats.bucket && ['elite', 'strong'].includes(replyStats.bucket) && counts.new > 0 && active === 'new' && (
          <p className="text-[11px] text-[#C29D52]/80 font-bold mb-4" data-testid="why-leads-banner">
            ⚡ You're getting enquiries because you're a fast responder.
          </p>
        )}

        {/* Filter pills */}
        <div className="sticky top-0 z-10 pt-2 pb-3 -mx-5 px-5 bg-[#0F1014]/95 backdrop-blur mb-4">
          <div className="flex gap-2 overflow-x-auto">
            {STATUS_TABS.map((t) => {
              const isActive = active === t.key;
              const count = counts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t.key)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition
                    ${isActive
                      ? 'text-white border-transparent'
                      : 'text-white/60 border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}
                  style={isActive ? { background: 'linear-gradient(135deg, #B89762, #C29D52)' } : undefined}
                  data-testid={`filter-${t.key}`}
                >
                  {t.label} {count > 0 && <span className={isActive ? 'opacity-90' : 'text-white/40'}>({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Multi-lead upgrade banner */}
        {showMultiLeadUpgrade && active === 'new' && (
          <div
            className="rounded-2xl p-4 mb-4 border border-[#B89762]/40/20 bg-gradient-to-r from-[#B89762]/10 to-[#B89762]/10"
            data-testid="multi-lead-upgrade-banner"
          >
            <div className="flex items-start gap-3">
              <Fire size={18} weight="fill" className="text-[#C29D52] mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-sm mb-0.5">You have {counts.new} enquiries waiting</p>
                <p className="text-white/60 text-xs mb-3">
                  Upgrade to handle more leads and never miss bookings.
                </p>
                <button
                  onClick={() => navigate('/studio-pass')}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                  data-testid="multi-lead-upgrade-btn"
                >
                  Upgrade
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        {leads === null && <p className="text-white/40 text-sm" data-testid="leads-loading">Loading…</p>}

        {leads !== null && visible.length === 0 && (
          <div className="rounded-2xl p-8 text-center bg-white/[0.02] border border-white/5" data-testid="leads-empty">
            <CheckCircle size={28} weight="fill" className="mx-auto mb-2 text-white/20" />
            <p className="text-white/60 font-bold text-sm mb-1">
              {active === 'new' ? "You're all caught up 🎉"
                : active === 'responded' ? 'No leads waiting on a reply'
                : 'No confirmed bookings yet'}
            </p>
            <p className="text-white/30 text-xs">
              {active === 'new' ? 'New enquiries will appear here.'
                : active === 'responded' ? 'Leads you\'ve replied to will live here.'
                : 'Your booked events will appear here once customers confirm.'}
            </p>
          </div>
        )}

        {visible.length > 0 && (
          <ul className="space-y-2" data-testid="leads-list">
            {visible.map((l) => (
              <li key={l.id}>
                <LeadRow lead={l} onOpen={() => navigate(`/chat/${l.chat_id}`)} />
              </li>
            ))}
          </ul>
        )}
      </div>
      {showSoftAsk && <PushSoftAsk context="supplier_lead" onClose={() => setShowSoftAsk(false)} />}
    </div>
  );
};

const LeadRow = ({ lead, onOpen }) => {
  const isUnread = (lead.unread_count || 0) > 0;
  const isNew = lead.status === 'new';
  const isBooked = lead.status === 'booked';
  const preview = lead.last_message || lead.first_message || '';

  return (
    <button
      onClick={onOpen}
      className={`w-full text-left rounded-2xl p-4 transition border
        ${isUnread
          ? 'bg-gradient-to-r from-[#B89762]/10 to-[#B89762]/5 border-[#B89762]/40/20'
          : 'bg-white/[0.03] border-white/5 hover:bg-white/[0.06]'}`}
      data-testid={`lead-row-${lead.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isNew && isUnread && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black text-[#C29D52]">
                <Fire size={10} weight="fill" /> NEW
              </span>
            )}
            {isBooked && (
              <span className="inline-flex items-center gap-1 text-[10px] font-black text-emerald-300">
                <CheckCircle size={10} weight="fill" /> CONFIRMED
              </span>
            )}
            <p className="font-bold text-sm truncate">
              {lead.customer_name}
              {lead.event_context?.event_name ? ` ${lead.event_context.event_name}` : ''}
              {lead.event_context?.event_type_emoji ? ` ${lead.event_context.event_type_emoji}` : ''}
            </p>
          </div>
          {lead.event_context && (lead.event_context.event_date || lead.event_context.event_postcode || lead.event_context.event_location) && (
            <p className="text-[11px] text-[#C29D52]/85/70 font-bold mb-1.5 truncate" data-testid="lead-event-context">
              {lead.event_context.event_date ? `📅 ${new Date(lead.event_context.event_date).toLocaleDateString('en-GB', {day:'numeric', month:'short'})}` : ''}
              {lead.event_context.event_location ? ` · 📍 ${lead.event_context.event_location}` : (lead.event_context.event_postcode ? ` · 📍 ${lead.event_context.event_postcode}` : '')}
            </p>
          )}
          {preview && (
            <p className="text-white/60 text-xs line-clamp-2 mb-2" data-testid="lead-preview">
              {preview.length > 140 ? `${preview.slice(0, 140)}…` : preview}
            </p>
          )}
          <div className="flex items-center gap-3 text-[10px] text-white/40 font-bold">
            <span className="inline-flex items-center gap-1">
              <Clock size={10} weight="bold" />
              {timeAgo(lead.last_customer_message_at || lead.created_at)}
            </span>
            {lead.status === 'responded' && !isUnread && (
              <span className="inline-flex items-center gap-1">
                <Lightning size={10} weight="fill" /> Waiting for reply
              </span>
            )}
            {lead.status === 'responded' && isUnread && (
              <span className="inline-flex items-center gap-1 text-[#C29D52]">
                <Lightning size={10} weight="fill" /> Customer replied
              </span>
            )}
          </div>
        </div>
        {!isBooked && (
          <div
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <PaperPlaneRight size={14} weight="fill" />
          </div>
        )}
      </div>
    </button>
  );
};

export default SupplierLeadsPage;

const ReplyTimePill = ({ stats }) => {
  const mins = Math.max(1, Math.round(stats.my_avg_seconds / 60));
  const bucket = stats.bucket; // elite | strong | average | needs_improvement | unranked
  const colors = {
    elite:   { bg: 'bg-emerald-400/10', border: 'border-emerald-400/30', icon: 'text-emerald-300' },
    strong:  { bg: 'bg-[#B89762]/40/10',    border: 'border-[#B89762]/45/30',    icon: 'text-[#C29D52]' },
    average: { bg: 'bg-amber-400/10',   border: 'border-amber-400/30',   icon: 'text-amber-300' },
    needs_improvement: { bg: 'bg-red-400/10', border: 'border-red-400/30', icon: 'text-red-300' },
    unranked: { bg: 'bg-white/5', border: 'border-white/10', icon: 'text-white/50' },
  }[bucket] || { bg: 'bg-white/5', border: 'border-white/10', icon: 'text-white/50' };

  const hint = (() => {
    if (stats.percentile !== null && stats.percentile >= 60) {
      return `Faster than ${stats.percentile}% of suppliers`;
    }
    if (stats.percentile !== null && stats.percentile < 40) {
      return 'Reply faster to increase bookings';
    }
    if (stats.my_reply_count < 3) return 'Need a few more replies for your rank';
    return 'Keep it up';
  })();

  return (
    <div
      className={`rounded-xl px-3 py-2.5 mb-4 border ${colors.bg} ${colors.border} flex items-center gap-2.5 text-xs`}
      data-testid="reply-time-pill"
    >
      <LightningIcon className={colors.icon} />
      <div className="flex-1 min-w-0">
        <p className="font-bold">Avg reply time: {mins} min{mins === 1 ? '' : 's'}</p>
        <p className="text-white/50 text-[11px]">{hint}</p>
      </div>
    </div>
  );
};

const LightningIcon = ({ className = '' }) => (
  <svg viewBox="0 0 256 256" className={`w-4 h-4 ${className}`} fill="currentColor">
    <path d="M213.85 125.46l-112 120a8 8 0 0 1-13.69-7l14.66-73.33L45.17 147a8 8 0 0 1-4.88-10.82L88.12 11.5a8 8 0 0 1 14.46-1.15l60.37 96.6 48.22 8.72a8 8 0 0 1 2.68 14.29Z"/>
  </svg>
);
